import React, { useEffect, useState } from "react"
import PropTypes from "prop-types"
import { Helmet } from "react-helmet"
import { useStaticQuery, graphql } from "gatsby"
import { getCurrentLang, isBrowser } from "../../services/Utils"
import { LANGUAGES, DEFAULT_LANG } from "../../constants/languages"

const SEO_LANGS = LANGUAGES.map(l => ({
  langCode: l.key,
  default: l.isDefault || false,
}))

function Seo({ title, meta, link, keywordsContent, descriptionContent, otherLangsUrls }) {
  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            title
            description
            author
          }
        }
      }
    `
  )

  const [currentLanguage, setCurrentLanguage] = useState("")
  const [interlinksArray, setInterlinksArray] = useState([])

  const generateInterlinkUrl = lang => {
    const origin = window.location.origin
    const paths = window.location.pathname.split("/").filter(Boolean)

    // Remove existing lang prefix if present
    if (paths[0]?.length === 2) {
      paths.shift()
    }

    // Add lang prefix for non-default languages
    const isDefault = lang.langCode === DEFAULT_LANG || lang.default
    if (!isDefault) {
      paths.unshift(lang.langCode)
    }

    return `${origin}/${paths.join("/")}`
  }

  const createInterlinks = (langs, linksPresent) => {
    const arr = []
    langs.forEach(lang => {
      const url = generateInterlinkUrl(lang)
      if (lang.default) {
        if (lang.langCode) {
          arr.push({ rel: "alternate", hreflang: lang.langCode, href: url })
        }
        arr.push({ rel: "alternate", hreflang: "x-default", href: url })
      } else {
        arr.push({
          rel: "alternate",
          hreflang: lang.langCode,
          href: linksPresent && lang.url ? lang.url : url,
        })
      }
    })
    setInterlinksArray(arr)
  }

  useEffect(() => {
    if (!isBrowser()) return

    const currentLang = getCurrentLang()
    setCurrentLanguage(currentLang)

    if (otherLangsUrls) {
      const currentUrl = generateInterlinkUrl({
        langCode: currentLang,
        default: currentLang === DEFAULT_LANG,
      })
      const containsCurrentLanguage = otherLangsUrls.some(
        lang => lang.url === currentUrl
      )
      const customInterlinks = [...otherLangsUrls]
      if (!containsCurrentLanguage) {
        customInterlinks.push({
          langCode: currentLang,
          url: currentUrl,
        })
      }
      customInterlinks.push({ default: true })
      createInterlinks(customInterlinks, true)
    } else {
      createInterlinks(SEO_LANGS)
    }
  }, [])

  const defaultTitle = site.siteMetadata?.title

  return (
    <Helmet
      htmlAttributes={{ lang: currentLanguage }}
      title={title}
      titleTemplate={defaultTitle ? `%s  ${defaultTitle}` : null}
      meta={[
        { name: "description", content: descriptionContent },
        { name: "keywords", content: keywordsContent },
        { property: "og:title", content: title },
        { property: "og:description", content: descriptionContent },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary" },
        { name: "twitter:creator", content: site.siteMetadata?.author || "" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: descriptionContent },
      ].concat(meta)}
      link={interlinksArray.concat(link)}
    />
  )
}

Seo.defaultProps = {
  meta: [],
  link: [],
}

Seo.propTypes = {
  title: PropTypes.string,
  meta: PropTypes.arrayOf(PropTypes.object),
  link: PropTypes.arrayOf(PropTypes.object),
  keywordsContent: PropTypes.string,
  descriptionContent: PropTypes.string,
  otherLangsUrls: PropTypes.arrayOf(PropTypes.object),
}

export default Seo
